import {ParseResult} from './markdown';

const bbCodes = ['indent', 'inset', 'read', 'quote', 'center', 'genericDescription', 'table', 'img', 'statblock-piece', 'list', 'item'];

/** теги без содержимого: `[img src="…" /]` закрывается сам */
const VOID_TAGS = new Set(['img']);

/**
 * Под каким именем тег уходит в дерево: `[table]` — это обёртка с подписью, а сама таблица
 * внутри неё тоже `table`, и имена не должны совпасть.
 */
const TREE_NAMES: Record<string, string> = {table: 'tableFigure', img: 'bookImage', 'statblock-piece': 'statblockPiece'};

/** атрибут тега: `[quote source="Эльминстер"]` — значение всегда в двойных кавычках */
const ATTRIBUTE = /([a-zA-Z][\w-]*)\s*=\s*"([^"]*)"/g;
/** флаг без значения: `[table headless]` */
const FLAG = /(?:^|\s)([a-zA-Z][\w-]*)(?=\s|$)/g;

/** имя тега и его атрибуты: всё, что записано внутри квадратных скобок */
function readTag(text: string): {name: string; props?: Record<string, string>} {
	const space = text.indexOf(' ');
	if (space === -1) return {name: text};
	const props: Record<string, string> = {};
	const rest = text.slice(space + 1);
	for (const found of rest.matchAll(ATTRIBUTE)) props[found[1]] = found[2];
	for (const found of rest.replace(ATTRIBUTE, ' ').matchAll(FLAG)) props[found[1]] = 'true';
	return {name: text.slice(0, space), props: Object.keys(props).length ? props : undefined};
}

export function parseBbCodes(content: string) {
	let openTags: string[] = [];
	let isDetectingTag = false;
	let isDetectingCloseTag = false;
	let detectingTag: string | null = null;

	const result: ParseResult[] = [];
	let resultCursor: ParseResult | null = null;

	const pushResult = (item: ParseResult) => {
		if (resultCursor) {
			if (resultCursor.tag !== 'TextNode') {
				const children = resultCursor.children || [];
				const newItem = {...item, parent: resultCursor};
				children.push(newItem);
				resultCursor.children = children;
				resultCursor = newItem;
			} else {
				if (resultCursor.parent) {
					const children = resultCursor.parent.children;
					const newItem = {...item, parent: resultCursor.parent};
					children?.push(newItem);
					resultCursor.parent.children = children;
					resultCursor = newItem;
				} else {
					result.push(item);
					resultCursor = result.at(-1)!;
				}
			}
			// В корень
		} else {
			result.push(item);
			resultCursor = result.at(-1)!;
		}
	};

	const addContentChar = (char: string) => {
		if (resultCursor) {
			resultCursor.content += char;
		} else {
			pushResult({tag: 'TextNode', content: char});
		}
	};

	const closeTag = () => {
		if (resultCursor) {
			if (resultCursor.tag === 'TextNode') {
				resultCursor = resultCursor.parent?.parent || null;
			} else {
				resultCursor = resultCursor.parent || null;
			}
		} else throw Error('Закрываем тэг, но курсор пуст');
	};

	for (let char of content) {
		// Если скобка открывается начинаем считывать тег
		if (char === '[') {
			isDetectingTag = true;
			// Если закончили считывать, сохраняем его
		} else if (char === ']' && detectingTag && isDetectingTag) {
			// Сейчас закрыт закрывающий тег
			if (isDetectingCloseTag) {
				// Если есть открытые теги
				if (!openTags.length || openTags.at(-1) !== detectingTag) {
					addContentChar(`[/${detectingTag}]`);
				} else if (openTags.length) {
					openTags.pop();
					closeTag();
				}
				detectingTag = null;
				isDetectingCloseTag = false;
				isDetectingTag = false;

				// Сейчас закрыт открывающий тег. Добавляем запись в result
			} else {
				// `[img … /]` закрывается сам: содержимого у него нет
				const selfClosing = detectingTag.trimEnd().endsWith('/');
				const {name, props} = readTag(selfClosing ? detectingTag.trimEnd().slice(0, -1).trimEnd() : detectingTag);
				if (bbCodes.includes(name)) {
					pushResult({tag: TREE_NAMES[name] || name, props});
					if (VOID_TAGS.has(name) || selfClosing) closeTag();
					else openTags.push(name);
				} else {
					addContentChar(`[${detectingTag}]`);
				}
				isDetectingTag = false;
				detectingTag = null;
			}

			// закрывающий тег — косая черта сразу за скобкой; дальше в теге она просто символ: `src="a/b"`
		} else if (isDetectingTag && char === '/' && detectingTag == null) {
			isDetectingCloseTag = true;
			// Если мы в режиме считывая тега, считываем
		} else if (isDetectingTag) {
			detectingTag = detectingTag == null ? char : detectingTag + char;
			// Находимся внутри тега, пишем его контент
		} else {
			// @ts-ignore
			if (resultCursor?.tag === 'TextNode') {
				addContentChar(char);
			} else {
				pushResult({tag: 'TextNode', content: char});
			}
		}
	}

	// скобка открылась и не закрылась: это обычный текст, а не тег, — терять его нельзя
	if (isDetectingTag) addContentChar(`[${isDetectingCloseTag ? '/' : ''}${detectingTag ?? ''}`);

	return result;
}
